/**
 * useTaskPhasePermissions - Hook de permissões granulares por fase de tarefa
 * 
 * Consome a RPC `get_task_phase_permissions_for_user` e expõe função
 * `canDoOnPhase(phaseId, action, isResponsible?)` para controlar ações no frontend.
 * 
 * Regras:
 * - Admins (root_admin, tenant_admin): acesso total (bypass no server-side)
 * - Supervisores/Agents: permissões configuradas na tabela task_phase_permissions
 * - Bypass do criador: verificado server-side (não precisa de lógica no hook)
 * - is_responsible_only: permite diferenciar permissões para responsável vs não responsável
 * - Sem perfil: deny by default (RPC retorna vazio)
 * 
 * @version 1.1.0
 * RELEASE: 2026-04-07 v1.0.0 - Criação inicial
 * RELEASE: 2026-04-07 v1.1.0 - Suporte a is_responsible_only com chave composta
 */

import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions } from '@/hooks/usePermissions';
import { createLogger } from '@/lib/logger';

/** Logger do módulo */
const logger = createLogger('useTaskPhasePermissions');

/** Tipos de ação que podem ser verificadas por fase */
export type PhaseAction =
  | 'can_view'
  | 'can_create'
  | 'can_edit'
  | 'can_delete'
  | 'can_change_phase'
  | 'can_change_status'
  | 'can_change_responsible'
  | 'can_comment'
  | 'can_reopen';

/** Registro de permissão por fase retornado pela RPC */
interface PhasePermissionRow {
  phase_id: string;
  phase_name: string;
  phase_color: string;
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_change_phase: boolean;
  can_change_status: boolean;
  can_change_responsible: boolean;
  can_comment: boolean;
  can_reopen: boolean;
  is_responsible_only: boolean;
}

/** Interface do retorno do hook */
export interface TaskPhasePermissions {
  /** 
   * Verifica se o usuário pode realizar uma ação em uma fase específica
   * @param phaseId - ID da fase
   * @param action - Ação a ser verificada
   * @param isResponsible - Se o usuário é o responsável pela tarefa (opcional)
   *   - true: busca linha com is_responsible_only=true, fallback para false
   *   - false: busca linha com is_responsible_only=false
   *   - undefined: retorna true se QUALQUER linha permite (mais permissivo, para UI sem contexto)
   */
  canDoOnPhase: (phaseId: string | null | undefined, action: PhaseAction, isResponsible?: boolean) => boolean;
  /** Retorna todas as permissões de uma fase específica */
  getPhasePermissions: (phaseId: string, isResponsible?: boolean) => PhasePermissionRow | undefined;
  /** Flag indicando se as permissões estão carregando */
  isLoading: boolean;
  /** Se o usuário é admin (acesso total) */
  isAdmin: boolean;
  /** Mapa de permissões por fase (para iterações) */
  permissionsMap: Map<string, PhasePermissionRow>;
}

/**
 * Gera chave composta para indexação no mapa
 * Formato: "phase_id:is_responsible_only"
 */
function makeKey(phaseId: string, isResponsible: boolean): string {
  return `${phaseId}:${isResponsible}`;
}

/**
 * Hook central de permissões granulares por fase
 * 
 * @param categoryId - Opcional: filtra fases por categoria
 */
export function useTaskPhasePermissions(categoryId?: string | null): TaskPhasePermissions {
  const { isAdmin, loading: permLoading } = usePermissions();

  // Busca permissões via RPC (cache 60s)
  const { data: rawData, isLoading: isQueryLoading } = useQuery({
    queryKey: ['task-phase-permissions', categoryId || 'all'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_task_phase_permissions_for_user', {
        p_category_id: categoryId || undefined,
      } as any);

      if (error) {
        logger.error('Erro ao buscar permissões por fase', error);
        throw error;
      }

      return (data || []) as PhasePermissionRow[];
    },
    staleTime: 60_000, // Cache 60s
    gcTime: 5 * 60_000,
    enabled: !permLoading,
  });

  // Mapa indexado por chave composta "phase_id:is_responsible_only" para acesso O(1)
  const permissionsMap = useMemo(() => {
    const map = new Map<string, PhasePermissionRow>();
    if (rawData) {
      for (const row of rawData) {
        // Chave composta para diferenciar linhas de responsável vs não responsável
        map.set(makeKey(row.phase_id, row.is_responsible_only), row);
        // Manter compatibilidade: chave simples aponta para a primeira linha encontrada (genérica)
        if (!map.has(row.phase_id)) {
          map.set(row.phase_id, row);
        }
      }
    }
    return map;
  }, [rawData]);

  /**
   * Verifica se o usuário pode realizar uma ação em uma fase
   * Deny by default: se a fase não tem registro, retorna false
   * 
   * Lógica de resolução:
   * - isResponsible = true: busca linha is_responsible_only=true, fallback para false
   * - isResponsible = false: busca linha is_responsible_only=false
   * - isResponsible = undefined: retorna true se QUALQUER linha permite (UI sem contexto)
   */
  const canDoOnPhase = useCallback(
    (phaseId: string | null | undefined, action: PhaseAction, isResponsible?: boolean): boolean => {
      // Sem phase_id = deny
      if (!phaseId) return false;

      // Admin = allow all (redundante, mas seguro para evitar flash)
      if (isAdmin) return true;

      // Ainda carregando = deny (previne flash de conteúdo não autorizado)
      if (isQueryLoading || permLoading) return false;

      if (isResponsible === undefined) {
        // Sem contexto de responsável: verifica AMBAS as linhas
        // Retorna true se qualquer uma permite (mais permissivo para UI)
        const permResp = permissionsMap.get(makeKey(phaseId, true));
        const permNotResp = permissionsMap.get(makeKey(phaseId, false));
        const respAllows = permResp ? (permResp[action] ?? false) : false;
        const notRespAllows = permNotResp ? (permNotResp[action] ?? false) : false;
        return respAllows || notRespAllows;
      }

      // Com contexto: busca linha específica, fallback para genérica
      const specificPerm = permissionsMap.get(makeKey(phaseId, isResponsible));
      if (specificPerm) {
        return specificPerm[action] ?? false;
      }

      // Fallback: busca linha genérica (is_responsible_only = false)
      const genericPerm = permissionsMap.get(makeKey(phaseId, false));
      if (genericPerm) {
        return genericPerm[action] ?? false;
      }

      return false;
    },
    [isAdmin, isQueryLoading, permLoading, permissionsMap]
  );

  /** Retorna registro completo de permissões da fase */
  const getPhasePermissions = useCallback(
    (phaseId: string, isResponsible?: boolean): PhasePermissionRow | undefined => {
      if (isAdmin) {
        // Admin sempre tem tudo — retorna registro sintético se não existir
        const existing = permissionsMap.get(phaseId);
        if (existing) return existing;
        return {
          phase_id: phaseId,
          phase_name: '',
          phase_color: '',
          can_view: true,
          can_create: true,
          can_edit: true,
          can_delete: true,
          can_change_phase: true,
          can_change_status: true,
          can_change_responsible: true,
          can_comment: true,
          can_reopen: true,
          is_responsible_only: false,
        };
      }

      if (isResponsible !== undefined) {
        const specific = permissionsMap.get(makeKey(phaseId, isResponsible));
        if (specific) return specific;
        // Fallback para genérica
        return permissionsMap.get(makeKey(phaseId, false));
      }

      return permissionsMap.get(phaseId);
    },
    [isAdmin, permissionsMap]
  );

  return {
    canDoOnPhase,
    getPhasePermissions,
    isLoading: isQueryLoading || permLoading,
    isAdmin,
    permissionsMap,
  };
}
